import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ledgerApi } from "@/api";
import { useLang, fmtDate } from "@/i18n";
import { PeriodFilter, yearStart, today, money } from "@/components/ledger/PeriodFilter";

const srcLink = (s) => (s.type === "document" || s.type === "settlement" ? `/documents/${s.id}` : s.type === "payslip" || s.type === "payslip_payment" ? "/payroll" : null);

export const AccountLedger = ({ initialCode }) => {
  const { t, lang } = useLang();
  const [accounts, setAccounts] = useState([]);
  const [code, setCode] = useState(initialCode);
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);

  useEffect(() => { ledgerApi.accounts().then(setAccounts); }, []);
  useEffect(() => { if (initialCode) setCode(initialCode); }, [initialCode]);
  useEffect(() => {
    if (!code) return;
    ledgerApi.ledger(code, { date_from: from || undefined, date_to: to || undefined }).then(setData).catch(() => toast.error(t("error")));
  }, [code, from, to, t]);

  return (
    <div className="space-y-4" data-testid="account-ledger-tab">
      <div className="flex flex-col lg:flex-row lg:items-end gap-3">
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-slate-500">{t("account")}</div>
          <Select value={code} onValueChange={setCode}>
            <SelectTrigger className="w-96 font-mono" data-testid="gl-account-select"><SelectValue placeholder={t("select_account")} /></SelectTrigger>
            <SelectContent className="max-h-80">{accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <PeriodFilter from={from} to={to} setFrom={setFrom} setTo={setTo} prefix="gl" />
      </div>
      {data && (
        <div className="erp-card overflow-hidden" data-testid="gl-table-card">
          <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap justify-between gap-2 text-sm">
            <span className="font-heading font-semibold">{data.account.code} · {data.account.name}</span>
            <span className="font-mono text-slate-500">{t("opening")}: <span data-testid="gl-opening">{money(data.opening) === "—" ? "0,00" : money(data.opening)}</span></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm erp-table">
              <thead>
                <tr className="text-left">
                  <th className="px-4 py-2">{t("date")}</th>
                  <th className="px-4 py-2">{t("description")}</th>
                  <th className="px-4 py-2 hidden md:table-cell">{t("account")}</th>
                  <th className="px-4 py-2 text-right">{t("debit")}</th>
                  <th className="px-4 py-2 text-right">{t("credit")}</th>
                  <th className="px-4 py-2 text-right">{t("closing")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                {data.lines.map((l, i) => {
                  const href = srcLink(l.source);
                  return (
                    <tr key={i} data-testid={`gl-row-${i}`}>
                      <td className="px-4 py-2 text-slate-500">{fmtDate(l.date, lang)}</td>
                      <td className="px-4 py-2 font-body">{href ? <Link to={href} className="text-blue-800 dark:text-blue-300 hover:underline">{l.description}</Link> : l.description}
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">{t(`src_${l.source.type}`)}</span></td>
                      <td className="px-4 py-2 hidden md:table-cell text-slate-500">{l.account_code}</td>
                      <td className="px-4 py-2 text-right">{money(l.debit)}</td>
                      <td className="px-4 py-2 text-right">{money(l.credit)}</td>
                      <td className="px-4 py-2 text-right font-medium">{money(l.balance) === "—" ? "0,00" : money(l.balance)}</td>
                    </tr>
                  );
                })}
                {data.lines.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500 font-body">{t("no_results")}</td></tr>}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 dark:bg-slate-900/60 font-mono font-semibold border-t-2 border-slate-200 dark:border-slate-700">
                  <td className="px-4 py-3 font-body" colSpan={3}>{t("total")}</td>
                  <td className="px-4 py-3 text-right">{money(data.total_debit)}</td>
                  <td className="px-4 py-3 text-right">{money(data.total_credit)}</td>
                  <td className="px-4 py-3 text-right" data-testid="gl-closing">{money(data.closing) === "—" ? "0,00" : money(data.closing)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
